import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';

import Page from 'components/layouts/page/Page';
import AccessGuard from 'components/shared/AccessGuard';
import Button from 'components/shared/Button';
import EditCostTiersButton from 'components/shared/highCostMeds/EditCostTiersButton';
import highCostMedListColumns from 'components/shared/highCostMeds/highCostMedListColumns';
import Search from 'components/shared/Search';
import ControlledTable from 'components/shared/table/ControlledTable';
import usePagination from 'hooks/usePagination';
import { HighCostMedicationListItem } from 'models/HighCostMedicationListItem';
import { PERMISSIONS } from 'models/Profile';
import {
  highCostMedicationListItemQueryKeys,
  indexHighCostMedicationListItems,
} from 'services/api/highCostMedicationListItems';
import { highCostMedicationTierQueryKeys, indexHighCostMedicationTiers } from 'services/api/highCostMedicationTiers';
import { serializeSort } from 'stores/helpers';
import { BodySmall, H2 } from 'styles/typography';
import PlusIcon from 'svg/PlusIcon';

import EditCostTiersModal from './EditCostTiersModal';
import HighCostMedEntryModal from './HighCostMedEntryModal';
import HighCostMedsEmptyState from './HighCostMedsEmptyState';
import HighCostMedsRowMenu from './HighCostMedsRowMenu';
import RemoveHighCostMedEntryModal from './RemoveHighCostMedEntryModal';

// Matches the API default (Date Added desc) so the first load mirrors the mock.
const DEFAULT_SORT: SortingState = [{ id: 'createdAt', desc: true }];

type ActiveModal =
  | { type: 'add' }
  | { type: 'edit'; item: HighCostMedicationListItem }
  | { type: 'remove'; item: HighCostMedicationListItem }
  | { type: 'tiers' };

function HighCostMedsPage() {
  const pagination = usePagination();
  const [sort, setSort] = useState<SortingState>(DEFAULT_SORT);
  const [searchInput, setSearchInput] = useState(''); // immediate input value
  const [search, setSearch] = useState(''); // committed (debounced) search term
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);

  // Debounce the commit so search and its relevance sort are applied together — the query never
  // sees a new search with a stale sort. Mirrors DenialListPage.
  const applySearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setSort(value ? [{ id: 'search', desc: true }] : DEFAULT_SORT);
      }, 500),
    []
  );

  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    applySearch(value);
  };

  const queryParams = useMemo(
    () => ({
      page: pagination.paging.page,
      pageSize: pagination.paging.pageSize,
      sortBy: serializeSort(sort),
      search,
    }),
    [pagination.paging, sort, search]
  );

  // Reset to the first page whenever the committed search changes.
  useEffect(() => {
    pagination.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: highCostMedicationListItemQueryKeys.index(queryParams),
    queryFn: ({ signal }) => indexHighCostMedicationListItems(queryParams, signal),
  });

  // The cost tier key. Fetched separately because customers never see the list entity that carries
  // it, and it is the same handful of rows on every page. See design 003.
  const { data: tierData } = useQuery({
    queryKey: highCostMedicationTierQueryKeys.index(),
    queryFn: ({ signal }) => indexHighCostMedicationTiers(signal),
  });

  const tiers = tierData?.data ?? [];

  // RowMenu is passed unconditionally: it returns null for a user who can neither edit nor remove,
  // so a view-only user gets no kebab.
  const columns = useMemo(
    () =>
      highCostMedListColumns({
        tiers,
        onEdit: (item) => setActiveModal({ type: 'edit', item }),
        onRemove: (item) => setActiveModal({ type: 'remove', item }),
        RowMenu: HighCostMedsRowMenu,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tierData]
  );

  const closeModal = () => setActiveModal(null);

  const totalRecords = data?.meta.totalRecords ?? 0;
  const showEmptyState = !isLoading && totalRecords === 0 && !search;

  return (
    <Page>
      <Page.Header>
        <HeaderRow>
          <TitleGroup>
            <Title>High Cost Meds List</Title>
            <SearchContainer>
              <Search value={searchInput} onChange={handleSearchChange} placeholder='Search medications' />
            </SearchContainer>
          </TitleGroup>
          {/* Each action carries its own gate, so a user who curates the tier key but does not
              maintain the medications sees only the link, and vice versa. A view-only user holds
              neither and gets a header with no actions, which is the correct read-only page. */}
          <HeaderActions>
            <AccessGuard accessor={PERMISSIONS.highCostMedicationListEdit}>
              {/* Disabled until the tier key has actually arrived. `tiers` cannot distinguish an
                  unresolved or failed query from a list whose medications genuinely carry no tiers, so
                  opening the form early would assert the No Cost Tiers state for an organization that
                  has them — and permanently so if the request failed rather than merely being slow. */}
              <EditCostTiersButton onClick={() => setActiveModal({ type: 'tiers' })} disabled={!tierData} />
            </AccessGuard>
            <AccessGuard accessor={PERMISSIONS.highCostMedicationListItemCreate}>
              <Button variant='primary' onClick={() => setActiveModal({ type: 'add' })}>
                <PlusIcon width={16} height={16} />
                Add New Medication
              </Button>
            </AccessGuard>
          </HeaderActions>
        </HeaderRow>
      </Page.Header>

      <Page.SubHeader>
        <BodySmall>
          {totalRecords} {totalRecords === 1 ? 'medication' : 'medications'} found
        </BodySmall>
      </Page.SubHeader>

      <Page.Content>
        {showEmptyState ? (
          <HighCostMedsEmptyState />
        ) : (
          <ControlledTable<HighCostMedicationListItem>
            data={data?.data ?? []}
            columns={columns}
            loading={isLoading}
            pageCount={data?.meta.totalPages ?? 1}
            pagination={pagination}
            sorting={{ sortBy: sort, setSortBy: setSort }}
            // Sorting is applied by the API and it has no fallback ORDER BY, so an unsorted list
            // paginates non-deterministically — rows repeat across pages and others never appear.
            // Keep every header click on a direction rather than cycling back to no sort.
            enableSortingRemoval={false}
          />
        )}
      </Page.Content>

      {activeModal?.type === 'add' && <HighCostMedEntryModal tiers={tiers} onClose={closeModal} />}
      {activeModal?.type === 'edit' && (
        <HighCostMedEntryModal highCostMedicationListItem={activeModal.item} tiers={tiers} onClose={closeModal} />
      )}
      {activeModal?.type === 'remove' && (
        <RemoveHighCostMedEntryModal highCostMedicationListItem={activeModal.item} onClose={closeModal} />
      )}
      {activeModal?.type === 'tiers' && <EditCostTiersModal tiers={tiers} onClose={closeModal} />}
    </Page>
  );
}

export default HighCostMedsPage;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const SearchContainer = styled.div`
  width: 280px;
`;

const Title = styled(H2)`
  font-size: 20px;
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
`;
